/**
 * Private access settings — query + mutation hooks (platform scope).
 *
 *   GET /api/admin/v1/private-access/certificate-record-check   (platform admin)
 *   PUT /api/admin/v1/private-access/certificate-record-check   (platform admin + step-up + interactive)
 *
 * The key travels in the PUT body only. The response never carries it, so nothing secret enters the
 * cache; the form keeps a typed key only until the save resolves.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import { certificateRecordCheckSchema, type CertificateRecordCheck, type CertificateRecordCheckUpdate } from "./schemas";

const BASE = "/api/admin/v1/private-access/certificate-record-check";

export async function fetchCertificateRecordCheck(): Promise<CertificateRecordCheck> {
  return certificateRecordCheckSchema.parse(await apiFetch<unknown>(BASE));
}

/** The check's setting and state; 60s while an administrator has the page open. */
export function useCertificateRecordCheckQuery() {
  return useQuery<CertificateRecordCheck, ApiError>({
    queryKey: queryKeys.privateAccess.certificateRecordCheck,
    queryFn: fetchCertificateRecordCheck,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Turn the check on or off; the server answers the new state. */
export function useSetCertificateRecordCheckMutation() {
  const qc = useQueryClient();
  return useMutation<CertificateRecordCheck, ApiError, CertificateRecordCheckUpdate>({
    mutationFn: (input) =>
      withStepUp(async () =>
        certificateRecordCheckSchema.parse(await apiFetch<unknown>(BASE, { method: "PUT", body: input })),
      ),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.privateAccess.certificateRecordCheck, data);
    },
  });
}
